/**
 * EmployeeFinder — cross-step employee search for the HR console.
 *
 * Layer 1: local fuzzy search over the cycle's employees (instant,
 * no network). Layer 2: "Ask AI" sends the query TEXT ONLY to the
 * parseEprSearchQuery Cloud Function (DeepSeek) and applies the
 * returned structured filter locally — employee data never leaves
 * the browser.
 *
 * Each result row shows the employee's status across steps; clicking
 * a status chip jumps the console into that step's focused view
 * (§L.3 — every row links to the specific record).
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { EPRSubmission } from '../../types/eprFramework';
import type { AppraisalForm } from '../../types/appraisal';
import type { AppealRecord } from '../../types/appeal';
import type { DirectoryEmployee } from '../../types/magicLink';
import { fetchDirectory } from '../../services/directoryService';
import {
  buildEmployeeIndex, fuzzyFilter, applyParsedFilter, describeParsedFilter,
  parseSearchQueryWithAi,
  type EmployeeSearchRow, type ParsedSearchFilter,
} from '../../services/smartSearchService';

const MAX_RESULTS = 30;

export interface EmployeeFinderProps {
  submissions: EPRSubmission[];
  appraisals: AppraisalForm[];
  /** Open appeals only (same list the console already loads). */
  openAppeals: AppealRecord[];
  /** Jump the console into the focused view for a step (1–7). */
  onJumpToStep: (step: number) => void;
}

const EmployeeFinder: React.FC<EmployeeFinderProps> = ({
  submissions, appraisals, openAppeals, onJumpToStep,
}) => {
  const [query, setQuery] = useState('');
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiFilter, setAiFilter] = useState<ParsedSearchFilter | null>(null);

  // Same lazy directory load the step cards use — emails for the rows.
  useEffect(() => {
    if (directory.length > 0 || submissions.length === 0) return;
    let alive = true;
    void fetchDirectory().then(r => { if (alive) setDirectory(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [directory.length, submissions.length]);

  const index = useMemo(
    () => buildEmployeeIndex(submissions, appraisals, openAppeals, directory),
    [submissions, appraisals, openAppeals, directory],
  );

  const results = useMemo(() => {
    if (aiFilter) return applyParsedFilter(index, aiFilter);
    if (query.trim()) return fuzzyFilter(index, query);
    return [];
  }, [index, query, aiFilter]);

  const handleAskAi = async () => {
    if (!query.trim() || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      setAiFilter(await parseSearchQueryWithAi(query));
    } catch (e) {
      setAiError((e as Error).message || 'AI parse failed');
    } finally {
      setAiBusy(false);
    }
  };

  const clearAi = () => { setAiFilter(null); setAiError(null); };

  const showResults = query.trim().length > 0 || aiFilter !== null;

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <span
            className="material-symbols-rounded absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            style={{ fontSize: 18 }}
            aria-hidden="true"
          >
            search
          </span>
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); clearAi(); }}
            onKeyDown={e => { if (e.key === 'Enter') void handleAskAi(); }}
            placeholder="Find an employee — or ask: siapa yang belum submit self review?"
            className="w-full rounded-lg border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
          />
        </div>
        <button
          type="button"
          onClick={() => void handleAskAi()}
          disabled={!query.trim() || aiBusy}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium border',
            'border-stone-300 text-stone-700 hover:border-stone-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          ].join(' ')}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
            {aiBusy ? 'progress_activity' : 'auto_awesome'}
          </span>
          Ask AI
        </button>
      </div>

      {aiError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          {aiError}
          <button type="button" onClick={clearAi} className="ml-2 underline font-medium">
            Dismiss
          </button>
        </div>
      )}

      {aiFilter && !aiError && (
        <div className="flex items-center gap-2 text-xs text-stone-600">
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-indigo-800">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              auto_awesome
            </span>
            AI filter: {describeParsedFilter(aiFilter)}
          </span>
          <button type="button" onClick={clearAi} className="underline hover:text-stone-800">
            Clear
          </button>
        </div>
      )}

      {showResults && (
        <div className="space-y-1.5">
          {results.length === 0 && (
            <div className="text-sm text-stone-500 px-1">No employees match.</div>
          )}
          {results.slice(0, MAX_RESULTS).map(r => (
            <FinderRow key={r.employeeId} row={r} onJumpToStep={onJumpToStep} />
          ))}
          {results.length > MAX_RESULTS && (
            <div className="text-xs text-stone-500 px-1">
              Showing {MAX_RESULTS} of {results.length} — narrow the search.
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// ─── Result row ─────────────────────────────────────────────────────

const FinderRow: React.FC<{
  row: EmployeeSearchRow;
  onJumpToStep: (step: number) => void;
}> = ({ row, onJumpToStep }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2">
    <div className="min-w-0">
      <div className="text-sm font-medium text-stone-900 truncate">{row.name}</div>
      <div className="text-xs text-stone-500 truncate">
        {[row.email, row.position, row.department].filter(Boolean).join(' · ') || row.employeeId}
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5">
      <StatusChip
        label={
          row.step1 === 'self_pending' ? 'Step 1 · self pending' :
          row.step1 === 'manager_pending' ? 'Step 1 · manager pending' :
          'Step 1 · submitted'
        }
        tone={row.step1 === 'submitted' ? 'success' : 'warning'}
        onClick={() => onJumpToStep(1)}
      />
      {row.signOff && (
        <StatusChip
          label={
            row.signOff === 'done' ? 'Step 5 · signed' :
            row.signOff === 'employee_signed' ? 'Step 5 · manager sign pending' :
            'Step 5 · sign pending'
          }
          tone={row.signOff === 'done' ? 'success' : 'warning'}
          onClick={() => onJumpToStep(5)}
        />
      )}
      {row.appealOpen && (
        <StatusChip label="Step 6 · appeal open" tone="alert" onClick={() => onJumpToStep(6)} />
      )}
      {row.hasOutcome && (
        <StatusChip label="Step 7 · outcome" tone="success" onClick={() => onJumpToStep(7)} />
      )}
    </div>
  </div>
);

const CHIP_TONES = {
  success: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:border-emerald-400',
  warning: 'bg-amber-50 text-amber-900 border-amber-200 hover:border-amber-400',
  alert:   'bg-red-50 text-red-800 border-red-200 hover:border-red-400',
} as const;

const StatusChip: React.FC<{
  label: string;
  tone: keyof typeof CHIP_TONES;
  onClick: () => void;
}> = ({ label, tone, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${CHIP_TONES[tone]}`}
  >
    {label}
  </button>
);

export default EmployeeFinder;
